import { rawRequest, uploadRequest } from './client';
import type { AddonIdentity, ClientJar, ManualAddon, ManualAddonKind } from '@mcpanel/core';
import type { TokenScope } from './types';
import type {
  AddonSourceName,
  ModResolveInput,
  ModResolveResult,
  ModSearchHit,
  OAuthConsent,
  OAuthGrant,
  PackStatus,
  RoutingStatus,
  ServerMods,
  Template,
  TemplateInput,
} from './types-p3';

const id = (value: string) => encodeURIComponent(value);

export interface ManualAddonReplaceResult {
  path: string;
  identity: AddonIdentity;
  replaced: Array<{ fileName: string; version?: string }>;
  trashed: Array<{ fileName: string; version?: string; trashedTo: string }>;
  restarted: boolean;
  note: string;
  /** ランチャーでも配っている Mod なら、配る写しも入れ替えた */
  distributed?: { side: ClientJar['side']; version?: string };
}

export const api3 = {
  modSources: () => rawRequest<{ sources: AddonSourceName[] }>('GET', '/mods/sources'),
  searchMods: (query: {
    q: string;
    loader: string;
    kind?: 'mod' | 'plugin';
    mcVersion: string;
    offset?: number;
    source?: AddonSourceName;
  }) => {
    const params = new URLSearchParams({ q: query.q, loader: query.loader, mcVersion: query.mcVersion });
    if (query.kind) params.set('kind', query.kind);
    if (query.source) params.set('source', query.source);
    if (query.offset) params.set('offset', String(query.offset));
    return rawRequest<ModSearchHit[]>('GET', `/mods/search?${params}`);
  },
  resolveMods: (input: ModResolveInput) => rawRequest<ModResolveResult>('POST', '/mods/resolve', input),
  serverMods: (serverId: string) => rawRequest<ServerMods>('GET', `/servers/${id(serverId)}/mods`),
  manualAddons: (serverId: string) =>
    rawRequest<{ addons: ManualAddon[]; unavailable?: string; distributed?: ClientJar[] }>(
      'GET',
      `/servers/${id(serverId)}/mods/manual`,
    ),
  /** 手動で入れた Mod の jar をランチャーでも配る（side）・配るのをやめる（null） */
  setManualAddonDistribution: (serverId: string, fileName: string, side: ClientJar['side'] | null) =>
    rawRequest<{ distributed: boolean; jar: ClientJar }>('PUT', `/servers/${id(serverId)}/mods/manual/distribution`, {
      fileName,
      side,
    }),
  replaceManualAddon: (serverId: string, input: { kind: ManualAddonKind; file: File; restart: boolean }) => {
    const params = new URLSearchParams({ kind: input.kind, fileName: input.file.name, restart: String(input.restart) });
    return uploadRequest<ManualAddonReplaceResult>(`/servers/${id(serverId)}/mods/manual?${params}`, input.file);
  },
  setPackPublished: (serverId: string, published: boolean) =>
    rawRequest<PackStatus>('PATCH', `/servers/${id(serverId)}/pack`, { published }),
  setPackAdminOnly: (serverId: string, adminOnly: boolean) =>
    rawRequest<PackStatus>('PATCH', `/servers/${id(serverId)}/pack`, { adminOnly }),

  templates: () => rawRequest<Template[]>('GET', '/templates'),
  createTemplate: (input: TemplateInput) => rawRequest<Template>('POST', '/templates', input),
  updateTemplate: (templateId: string, input: TemplateInput) =>
    rawRequest<Template>('PUT', `/templates/${id(templateId)}`, input),
  deleteTemplate: (templateId: string) => rawRequest<void>('DELETE', `/templates/${id(templateId)}`),

  routing: () => rawRequest<RoutingStatus>('GET', '/routing'),
  applyEdge: () => rawRequest<RoutingStatus>('POST', '/routing/apply', {}),

  oauthGrants: () => rawRequest<OAuthGrant[]>('GET', '/me/oauth-clients'),
  revokeOAuthGrant: (grantId: string) => rawRequest<void>('DELETE', `/me/oauth-clients/${id(grantId)}`),
  oauthConsent: (requestId: string) => rawRequest<OAuthConsent>('GET', `/oauth/consent/${id(requestId)}`),
  decideOAuthConsent: (requestId: string, approve: boolean, scopes: TokenScope[]) =>
    rawRequest<{ redirectTo: string }>('POST', `/oauth/consent/${id(requestId)}`, { approve, scopes }),
};
